import Head from 'next/head'
import { useRouter } from 'next/router'
import styled from 'styled-components'

import { ArticleTile } from 'editorial/components/ArticleTile'
import { EditorialHero } from 'editorial/components/EditorialHero'
import { serverSideConfigs } from 'localization'
import ErrorPage from 'pages/_error'
import { serverRequestService } from 'server/services/serverRequestService'
import { createMosaicClient } from 'server/utils/mosaicClient'
import { CMSArticleType } from 'shared/enums/CMSArticleType'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { colors, media } from 'shared/lib'
import { Article } from 'shared/types/Article'
import { ArticleBanner } from 'shared/types/ArticleBanner'
import { FCGetServerSidePropsContext, getServerPageProps } from 'shared/utils/getServerSideProps'

type EditorialPageFcFridaysProps = {
  articles: Array<Article>
  banner: Article
}

const EditorialPageFcFridays = ({ articles, banner }: EditorialPageFcFridaysProps) => {
  const router = useRouter()
  if (articles === null) {
    return <ErrorPage statusCode={404} />
  }

  return (
    <>
      <Head>
        <link
          rel="canonical"
          href={`https://www.flightclub.com${router?.asPath}`}
          key="canonical"
        />
      </Head>
      <Page>
        <EditorialHero banners={banner?.sections as ArticleBanner[]} />
        <ArticlesContainer data-qa="EditorialArticles">
          {articles?.length > 0 &&
            articles.map((article, i) => (
              <ArticleTile article={article} key={article.uuid} index={i} />
            ))}
        </ArticlesContainer>
      </Page>
    </>
  )
}

export const getServerSideProps = async (context: FCGetServerSidePropsContext) => {
  const { req, res, locale } = context

  try {
    const { data: dataArticles } = await serverRequestService(req, res, 'cms').get(
      `/api/v1/cms/cms_articles_by_group`,
      {
        params: { group: CMSArticleType.Editorial, limit: 30, sortBy: 2 },
      },
    )

    const mosaicClient = createMosaicClient(req, locale)
    const { data: dataBanner } = await mosaicClient.get(
      '/api/v1/mosaic/articles/banners/fc-editorial-landing',
    )

    if (!req.featureFlags?.[FeatureFlag.TEMP_WEB_FC_EDITORIALS_NAVIGATION]) {
      return { notFound: true }
    }

    if (
      !req.featureFlags?.[FeatureFlag.TEMP_WEB_FC_EDITORIALS] ||
      !dataArticles ||
      !dataArticles?.articles
    ) {
      return {
        props: {
          ...(await getServerPageProps(context)),
          ...(await serverSideConfigs(res, locale)),
          articles: null,
          banner: null,
        },
      }
    }

    return {
      props: {
        ...(await getServerPageProps(context)),
        ...(await serverSideConfigs(res, locale)),
        articles: dataArticles.articles,
        banner: dataBanner,
      },
    }
  } catch (error) {
    return {
      props: {
        ...(await getServerPageProps(context)),
        ...(await serverSideConfigs(res, locale)),
        articles: null,
        banner: null,
      },
    }
  }
}

const ArticlesContainer = styled.div`
  ${media.medium`
    column-gap: 24px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    width: 948px;
  `};
`
const Page = styled.div`
  align-items: center;
  background-color: ${colors.FC2_WHITE};
  display: flex;
  flex-direction: column;
`

export default EditorialPageFcFridays
